import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { SymbolSelector } from "./SymbolSelector";
import {
  useGetInstrumentSuggestion,
  useGetTrendingSymbols,
} from "@/api/symbolApi";
import { useUserContext } from "@/contexts/userContext";
import { usePostCopyStrategy } from "@/api/strategyApi";
import { toast } from "../ui/use-toast";
import { useRouter } from "next/router";
import { randomString } from "@/helpers/helperFunctions";

const SymbolUpdateModal = ({
  isOpen,
  setIsOpen,
  strategy,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  strategy: any;
}) => {
  const { user } = useUserContext();
  const router = useRouter();

  const [selectedSymbol, setSelectedSymbol] = useState<string>("");
  const [trendingSymbols, setTrendingSymbols] = useState<
    { name: string; symbol: string; short_name: string }[]
  >([]);
  const [symbolData, setSymbolData] = useState<
    { name: string; symbol: string; short_name: string }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const {
    data: trendingSymbolsData,
    isSuccess: isTrendingSymbolSuccess,
    isLoading: isTrendingSymbolLoading,
  } = useGetTrendingSymbols("forex");

  const {
    mutateAsync: copyStrategy,
    data: copyStrategyData,
    isSuccess: isCopySuccess,
    isPending: isCopyPending,
  } = usePostCopyStrategy();

  const {
    data: instrumentSuggestionData,
    isSuccess: isInstrumentSuggestionSuccess,
    isLoading: isInstrumentSuggestionLoading,
  } = useGetInstrumentSuggestion(user?.user_id);

  const copyHandler = async () => {
    if (!user?.user_id || !strategy?.keygen || !selectedSymbol) return;
    setLoading(true);
    try {
      await copyStrategy({
        userId: user?.user_id,
        key: strategy?.keygen,
        symbol: selectedSymbol,
      });
    } catch {
      toast({
        title: "Error copying strategy",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isTrendingSymbolSuccess && trendingSymbolsData) {
      setTrendingSymbols(
        Object.entries(
          trendingSymbolsData.data as { [key: string]: string }
        ).map(([symbol, name], index: number) => {
          return { name, symbol, short_name: symbol };
        })
      );
    }
  }, [isTrendingSymbolSuccess, trendingSymbolsData, isTrendingSymbolLoading]);

  useEffect(() => {
    if (isInstrumentSuggestionSuccess && instrumentSuggestionData) {
      if (Array.isArray(instrumentSuggestionData.data)) {
        setSymbolData([...instrumentSuggestionData.data]);
      }
    }
  }, [
    isInstrumentSuggestionSuccess,
    instrumentSuggestionData,
    isInstrumentSuggestionLoading,
  ]);

  useEffect(() => {
    if (isCopySuccess && copyStrategyData && user?.user_id) {
      toast({
        title: "Strategy copied successfully",
        variant: "default",
      });
      setTimeout(() => {
        router.push(
          "/edit-strategy/" +
            encodeURIComponent(user?.user_id) +
            "/" +
            copyStrategyData?.data?.id +
            randomString(5) +
            "/" +
            user.token +
            "/false" +
            `?ref=${router.asPath}&locale=${router.query.locale || "en"}`
        );
        setLoading(false);
      }, 1000);
    }
  }, [isCopySuccess, isCopyPending]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="p-6  flex-col justify-between flex">
        <DialogHeader>
          <DialogTitle>
            <div className="text-sm">
              {`Copying strategy - ${strategy?.strategy_name}, ${strategy?.symbol}`}
            </div>
          </DialogTitle>
          <DialogDescription className="flex flex-col gap-12">
            <div className="mt-5 flex flex-col gap-4">
              <h1 className="font-bold text-lg text-[#fff]">
                {"Choose Symbol"}
              </h1>
              <p>{`The current symbol - ${strategy?.symbol}, is not supported by your broker. Please choose a different symbol from the list to proceed with copying.`}</p>
              <SymbolSelector
                trendingSymbols={trendingSymbols}
                value={selectedSymbol}
                clearSymbol={() => setSelectedSymbol("")}
                loading={isTrendingSymbolLoading}
                onChange={setSelectedSymbol}
                symbolData={symbolData}
                placeholder="Search for Symbol"
                t={(value: string) => value}
              ></SymbolSelector>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <div className="w-full flex mt-4 align-right justify-end">
            <div className="text-right flex gap-4">
              <Button variant={"secondary"} onClick={() => setIsOpen(false)}>
                {"Cancel"}
              </Button>
              <Button
                disabled={!selectedSymbol}
                loadingText={loading}
                onClick={() => copyHandler()}
              >
                {"Copy strategy"}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SymbolUpdateModal;
